/**
 * Video Section - Responsive Video Handler
 * Handles mobile/desktop video switching based on viewport
 * Handles autoplay functionality
 */

class ResponsiveVideoSection {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.breakpoint = 768;
    this.media = document.querySelector(`#section-video-${sectionId} deferred-media[data-has-mobile-video]`);
    
    if (!this.media) return;
    
    this.template = this.media.querySelector('template');
    this.poster = this.media.querySelector('[id^="Deferred-Poster-"]');
    this.autoplay = this.media.dataset.autoplay === 'true';
    this.hideControls = this.media.dataset.hideControls === 'true';
    this.init();
  }
  
  isMobile() {
    return window.innerWidth < this.breakpoint;
  }
  
  getVideoConfig() {
    const mobile = this.isMobile();
    return {
      isMobile: mobile,
      videoType: mobile ? this.media.dataset.mobileVideoType : this.media.dataset.desktopVideoType,
      videoSrc: mobile ? this.media.dataset.mobileVideoSrc : this.media.dataset.desktopVideoSrc,
      description: mobile ? (this.media.dataset.descriptionMobile || this.media.dataset.description) : this.media.dataset.description,
      loop: this.media.dataset.loop === 'true'
    };
  }
  
  createVideoElement(config) {
    if (config.videoType === 'shopify') {
      const video = document.createElement('video');
      video.src = config.videoSrc;
      video.autoplay = true;
      video.loop = config.loop;
      video.controls = !this.hideControls;
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      // Ensure video is visible
      video.style.position = 'absolute';
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.top = '0';
      video.style.left = '0';
      video.style.objectFit = 'cover';
      return video;
    } else {
      const iframe = document.createElement('iframe');
      iframe.src = config.videoSrc;
      iframe.className = config.videoType === 'youtube' ? 'js-youtube' : 'js-vimeo';
      iframe.allow = 'autoplay; encrypted-media';
      iframe.allowFullscreen = true;
      iframe.title = config.description || '';
      // Ensure iframe is visible
      iframe.style.position = 'absolute';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.top = '0';
      iframe.style.left = '0';
      iframe.style.border = 'none';
      return iframe;
    }
  }
  
  loadVideo(focus = true) {
    if (this.media.hasAttribute('loaded')) return;
    
    if (window.pauseAllMedia) {
      window.pauseAllMedia();
    }
    
    const config = this.getVideoConfig();
    
    // Safety check - if no video source, fall back to template content
    if (!config.videoSrc || config.videoSrc === '') {
      this.media.setAttribute('loaded', 'true');
      if (this.template && this.template.content.firstElementChild) {
        const content = this.template.content.firstElementChild.cloneNode(true);
        this.media.appendChild(content);
        if (focus) content.focus();
      }
      return;
    }
    
    const videoElement = this.createVideoElement(config);
    
    this.media.setAttribute('loaded', 'true');
    this.media.appendChild(videoElement);
    
    if (focus) videoElement.focus();
    
    // Force play for videos
    if (videoElement.nodeName === 'VIDEO' && videoElement.autoplay) {
      videoElement.play().catch(() => {
        // Autoplay was prevented, user will need to interact
      });
    }
  }
  
  init() {
    // Remove existing click listener and add our own
    if (this.poster) {
      // Clone and replace to remove existing listeners
      const newPoster = this.poster.cloneNode(true);
      this.poster.parentNode.replaceChild(newPoster, this.poster);
      this.poster = newPoster;
      
      // Add our custom click handler
      this.poster.addEventListener('click', (e) => {
        e.preventDefault();
        this.loadVideo(true);
      });
    }
    
    // If autoplay is enabled, trigger load immediately
    if (this.autoplay) {
      requestAnimationFrame(() => {
        this.loadVideo(false);
      });
    }
  }
}

// Autoplay handler for non-responsive videos
class VideoAutoplay {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.media = document.querySelector(`#section-video-${sectionId} deferred-media[data-autoplay="true"]:not([data-has-mobile-video])`);
    
    if (!this.media) return;
    
    // Trigger loadContent immediately for autoplay
    if (this.media.loadContent) {
      this.media.loadContent(false);
    } else {
      // Fallback if loadContent doesn't exist yet
      this.media.setAttribute('loaded', '');
      const template = this.media.querySelector('template');
      if (template) {
        const content = template.content.firstElementChild.cloneNode(true);
        this.media.appendChild(content);
      }
    }
  }
}

// Initialize function to be called from section
window.initResponsiveVideo = function(sectionId) {
  new ResponsiveVideoSection(sectionId);
};

// Initialize autoplay for non-responsive videos
window.initVideoAutoplay = function(sectionId) {
  new VideoAutoplay(sectionId);
};
